import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ShieldCheck as ShieldCheckIcon } from 'lucide-react'
import { api } from '../lib/api'
import { queries } from '../lib/queries'
import { getOptimizedIconUrl } from '../lib/utils'
import { useSubscription } from '../context/SubscriptionContext'

export function RulesListClient() {
  const queryClient = useQueryClient()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const { canPerformPaidActions, isLoading: subscriptionLoading } = useSubscription()
  const showSubscriptionRestriction = !subscriptionLoading && !canPerformPaidActions

  const { data: policiesData, error: queryError, isLoading } = useQuery({
    queryKey: queries.policies.list(),
    queryFn: () => api.getPolicies(),
  })

  const policies = policiesData?.policies ?? []
  const error = queryError?.message ?? null

  const handleDelete = async (id: string) => {
    setDeleting(true)
    try {
      await api.deletePolicy(id)
      queryClient.invalidateQueries({ queryKey: queries.policies.list() })
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete rule')
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Rules</h1>
        {showSubscriptionRestriction ? (
          <button
            type="button"
            disabled
            title="Active subscription required"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium opacity-50 cursor-not-allowed text-sm"
          >
            Create Rule
          </button>
        ) : (
          <Link
            to="/rules/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors text-sm"
          >
            Create Rule
          </Link>
        )}
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
              <div className="motion-safe:animate-pulse space-y-3">
                <div className="h-5 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-100 rounded w-1/3" />
                <div className="flex gap-1.5 pt-1">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                  <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                  <div className="w-8 h-8 bg-gray-200 rounded-lg" />
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <div className="h-9 bg-gray-100 rounded-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(error || deleteError) && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{error || deleteError}</p>
        </div>
      )}

      {!isLoading && policies.length === 0 && !error ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <ShieldCheckIcon size={48} className="text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No rules yet. Create your first rule to start managing app and website restrictions.</p>
          {showSubscriptionRestriction ? (
            <>
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium opacity-50 cursor-not-allowed"
              >
                Create Rule
              </button>
              <p className="text-xs text-amber-700 mt-2">Reactivate subscription to create rules.</p>
            </>
          ) : (
            <Link
              to="/rules/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors"
            >
              Create Rule
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {policies.map((policy) => {
            const isSystem = !policy.familyId
            const isAssigned = (policy.assignedDeviceCount ?? 0) > 0
            const apps = policy.apps ?? []
            const config = policy.config as Record<string, unknown>
            const mode = config?.mode === 'allowlist' ? 'allowlist' : 'blocklist'
            const appCount = apps.length

            return (
              <div
                key={policy.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{policy.name}</h3>
                      {isSystem && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          System
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {mode === 'blocklist' ? 'Blocks' : 'Allows'} {appCount} app{appCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {apps.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-3">
                    {apps.slice(0, 3).map((app) => {
                      const iconUrl = getOptimizedIconUrl(app.icon, 60)
                      return iconUrl ? (
                        <img
                          key={app.bundleId || app.packageName || app.name}
                          src={iconUrl}
                          alt={app.name}
                          width={32}
                          height={32}
                          className="w-8 h-8 rounded-lg"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          key={app.bundleId || app.packageName || app.name}
                          className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-500"
                        >
                          {app.name.charAt(0)}
                        </div>
                      )
                    })}
                    {apps.length > 3 && (
                      <span className="text-xs text-gray-400 ml-1">+{apps.length - 3} more</span>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-2">
                  <Link
                    to="/rules/$id"
                    params={{ id: policy.id }}
                    className="flex-1 text-center px-3 py-2 text-sm font-medium text-brand bg-brand-light rounded-lg hover:bg-brand/10 transition-colors"
                  >
                    View
                  </Link>
                  {!isSystem && (
                    <button
                      onClick={() => setDeleteId(policy.id)}
                      disabled={isAssigned}
                      title={isAssigned ? 'Remove this rule from all devices before deleting' : undefined}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isAssigned
                          ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                          : 'text-red-600 bg-red-50 hover:bg-red-100'
                      }`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Rule</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this rule? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
